import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';
import SectionContainer from '../../containers/PageBuilder/SectionBuilder/SectionContainer';
import { NamedLink } from '../../components';

import { BENEFITS, PROOF_POINTS, TRUST_NOTE } from '../benefits';
import css from './SectionBenefits.module.css';

/**
 * Haken vor jedem Argument. Als Zeichnung und nicht als Schriftzeichen,
 * damit er in jeder Schriftart gleich aussieht.
 */
const Check = () => (
  <svg
    className={css.check}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="9" cy="9" r="9" className={css.checkDisc} />
    <path
      d="M5 9.2 7.6 11.8 13 6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Der Pfeil am Kartenkopf. Nur auf schmalen Schirmen sichtbar - dort ist er
 * das einzige Zeichen dafuer, dass sich hinter der Ueberschrift noch etwas
 * verbirgt.
 */
const Pfeil = () => (
  <svg width="14" height="9" viewBox="0 0 14 9" aria-hidden="true" focusable="false">
    <path
      d="M1 1.5 7 7.5 13 1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Schmale Schirme erkennen - aber nur fuer die Vorlesehilfen.
 *
 * Ob eine Karte auf- oder zugeklappt ist, entscheidet allein das
 * Stylesheet: zu auf dem Telefon, offen ab Tablet. Deshalb springt beim
 * Laden nichts. Dieser Haken liefert nur die Zusatzangaben fuer
 * Screenreader und Tastatur, damit auf dem grossen Schirm kein Knopf
 * angekuendigt wird, den es dort gar nicht gibt.
 */
const useSchmal = () => {
  const [schmal, setSchmal] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const frage = window.matchMedia('(max-width: 767px)');
    const merken = () => setSchmal(frage.matches);
    merken();
    if (frage.addEventListener) {
      frage.addEventListener('change', merken);
      return () => frage.removeEventListener('change', merken);
    }
    frage.addListener(merken);
    return () => frage.removeListener(merken);
  }, []);

  return schmal;
};

/**
 * FairwayBid: Zwei Zielgruppen, zwei Argumentationen.
 *
 * Ein Besucher fragt in den ersten Sekunden nicht, was FairwayBid ist,
 * sondern was er davon hat. Für eine Golfanlage ist die Antwort eine ganz
 * andere als für einen Händler. Deshalb stehen hier zwei Karten
 * nebeneinander statt einer gemeinsamen Aufzählung – und jeder erkennt
 * sofort, welche seine ist.
 *
 * Die Händlerkarte steht auf dunklem Grund. Nicht als Verzierung: Sie
 * trennt die beiden Seiten auf einen Blick, noch bevor jemand ein Wort
 * gelesen hat.
 *
 * Überschrift und Einleitung kommen weiter aus der Console, damit Tom sie
 * ohne Code ändern kann. Die Argumente stehen in src/fairwaybid/benefits.js.
 */
const SectionBenefits = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  const schmal = useSchmal();
  const [offene, setOffene] = useState({});
  const umschalten = key => setOffene(vorher => ({ ...vorher, [key]: !vorher[key] }));

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, css.underHero)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {/*
        Eigene Kopfzeile statt der Standardgestaltung: Der Satz "Ein
        Marktplatz, zwei Seiten" ist die Kernaussage des Abschnitts und stand
        vorher klein und linksbündig da wie eine Nebenbemerkung. Jetzt steht
        er mittig über den beiden Karten und nimmt deren Symmetrie vorweg -
        die Aussage und die Anordnung sagen dasselbe.
      */}
      {hasHeaderFields ? (
        <header className={css.header}>
          <Field data={title} className={css.title} options={fieldOptions} />
          <span className={css.rule} aria-hidden="true" />
          <Field data={description} className={css.intro} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}

      <div className={css.wrap}>
        {/*
          Das Zahlenband steht vor den Argumenten, nicht dahinter: Es
          beantwortet die erste stumme Frage - gibt es hier ueberhaupt schon
          jemanden? Bewusst ohne Kaesten, damit es ein ruhiges Band bleibt
          und die beiden Karten darunter die eigentlichen Objekte sind.
        */}
        <dl className={css.proof}>
          {PROOF_POINTS.map(punkt => (
            <div key={punkt.label} className={css.proofItem}>
              <dt className={css.proofValue}>{punkt.value}</dt>
              <dd className={css.proofLabel}>
                {punkt.label}
                <span className={css.proofNote}>{punkt.note}</span>
              </dd>
            </div>
          ))}
        </dl>

        <div className={css.grid}>
          {BENEFITS.map(seite => {
            const istHaendler = seite.key === 'dealer';
            const koerperId = `fwb-benefit-${seite.key}`;
            const istOffen = !!offene[seite.key];
            return (
              <article
                key={seite.key}
                className={classNames(css.card, { [css.cardDark]: istHaendler })}
              >
                {/*
                  Auf dem Telefon standen hier zwei Karten mit je fuenf
                  Argumenten untereinander - wer die Haendlerseite sehen
                  wollte, musste an der Clubseite vorbeiscrollen. Jetzt sind
                  beide Ueberschriften auf einen Blick da und man klappt auf,
                  was einen angeht. Ab Tablet stehen die Karten nebeneinander
                  und sind immer offen; der Knopf ist dann nur noch die
                  Ueberschrift.
                */}
                <h3 className={css.kopfZeile}>
                  <button
                    type="button"
                    className={css.kopf}
                    onClick={() => umschalten(seite.key)}
                    aria-expanded={schmal ? istOffen : undefined}
                    aria-controls={schmal ? koerperId : undefined}
                    tabIndex={schmal ? 0 : -1}
                  >
                    <span className={css.kopfText}>
                      <span className={css.eyebrow}>{seite.eyebrow}</span>
                      <span className={css.headline}>{seite.headline}</span>
                    </span>
                    <span
                      className={classNames(css.pfeil, { [css.pfeilOffen]: istOffen })}
                      aria-hidden="true"
                    >
                      <Pfeil />
                    </span>
                  </button>
                </h3>

                <div
                  id={koerperId}
                  className={classNames(css.koerper, { [css.koerperOffen]: istOffen })}
                >
                  <p className={css.lead}>{seite.lead}</p>

                  <ul className={css.points}>
                    {seite.points.map(punkt => (
                      <li key={punkt} className={css.point}>
                        <Check />
                        <span>{punkt}</span>
                      </li>
                    ))}
                  </ul>

                  <NamedLink
                    name="SignupPage"
                    className={classNames(css.cta, { [css.ctaDark]: istHaendler })}
                  >
                    {seite.ctaLabel}
                  </NamedLink>
                </div>
              </article>
            );
          })}
        </div>

        <p className={css.trust}>{TRUST_NOTE}</p>
      </div>
    </SectionContainer>
  );
};

export default SectionBenefits;
